import React, { useState, useEffect } from 'react';
import { Container, Card, Button, Spinner, Alert, Form, Row, Col, ListGroup, Image, Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { League, Castaway, Draft } from '../types';

const AdminStandingsEmail: React.FC = () => {
  const [leagues, setLeagues] = useState<League[]>([]);
  const [selectedLeagueId, setSelectedLeagueId] = useState<string>('');
  const [castaways, setCastaways] = useState<Castaway[]>([]);
  const [drafts, setDrafts] = useState<{ [key: string]: Draft }>({});
  const [scores, setScores] = useState<any>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailHtml, setEmailHtml] = useState<string>('');
  const [episodeNumber, setEpisodeNumber] = useState<string>('');
  const [eliminationPicks, setEliminationPicks] = useState<any>({});
  const [isCopied, setIsCopied] = useState(false);
  const [episodeDeadlines, setEpisodeDeadlines] = useState<{ [key: string]: string } | undefined>({});
  const [adminSeason, setAdminSeason] = useState<number>(() => parseInt(localStorage.getItem('adminSelectedSeason') || '50'));

  const handleCopy = () => {
    navigator.clipboard.writeText(emailHtml).then(() => {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000); // Reset after 2 seconds
    }, (err) => {
      console.error('Could not copy text: ', err);
    });
  };

  const fetchLeagues = async (season: number) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/leagues/all`);
      const data = await response.json();
      const filteredLeagues = (data.leagues || []).filter((l: any) => l.season === season);
      setLeagues(filteredLeagues);
    } catch (err) {
      setError('Failed to fetch leagues.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLeagues(adminSeason);

    const handleSeasonChange = () => {
      const newSeason = parseInt(localStorage.getItem('adminSelectedSeason') || '50');
      setAdminSeason(newSeason);
      fetchLeagues(newSeason);
      setSelectedLeagueId('');
      setEmailHtml('');
    };
    window.addEventListener('adminSeasonChanged', handleSeasonChange);
    return () => window.removeEventListener('adminSeasonChanged', handleSeasonChange);
  }, []);

  const handleGenerateEmail = async () => {
    if (!selectedLeagueId) {
      setError('Please select a league.');
      return;
    }
    if (!episodeNumber) {
      setError('Please enter the episode number.');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const [draftsResponse, castawaysResponse, scoresResponse, eliminationPicksResponse, leagueResponse] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL}/api/leagues/${selectedLeagueId}/drafts`),
        fetch(`${process.env.REACT_APP_API_URL}/api/leagues/${selectedLeagueId}/castaways`),
        fetch(`${process.env.REACT_APP_API_URL}/api/scores/${adminSeason}`),
        fetch(`${process.env.REACT_APP_API_URL}/api/leagues/${selectedLeagueId}/elimination-picks`),
        fetch(`${process.env.REACT_APP_API_URL}/api/leagues/all`),
      ]);
      const draftsData = await draftsResponse.json();
      const castawaysData = await castawaysResponse.json();
      const scoresData = await scoresResponse.json();
      const eliminationPicksData = await eliminationPicksResponse.json();
      const leagueData = await leagueResponse.json();
      const currentLeague = leagueData.leagues.find((l: any) => l.id === selectedLeagueId);

      setDrafts(draftsData.drafts || {});
      setCastaways(castawaysData.castaways || []);
      setScores(scoresData.scores || {});
      setEliminationPicks(eliminationPicksData.eliminationPicks || {});
      setEpisodeDeadlines(currentLeague.episodeDeadlines || {});

      generateEmailHtml(draftsData.drafts || {}, castawaysData.castaways || [], scoresData.scores || {}, episodeNumber, eliminationPicksData.eliminationPicks || {}, currentLeague.episodeDeadlines || {});
    } catch (err) {
      setError('Failed to generate email.');
    } finally {
      setIsLoading(false);
    }
  };

  const calculateScore = (castawayId: number, allScores: any, episodeRange?: { start?: number, end?: number }): number => {
    let totalScore = 0;
    Object.entries(allScores).forEach(([episode, episodeScores]: [string, any]) => {
      const episodeNum = parseInt(episode.replace('episode', ''), 10);
      if (episodeNum === 1) return; // Episode 1 is non-scoring tracking
      if (episodeRange) {
        if (episodeRange.start && episodeNum < episodeRange.start) return;
        if (episodeRange.end && episodeNum > episodeRange.end) return;
      }
      const scores = episodeScores[castawayId];
      if (scores) {
        totalScore += (scores.tribeImmunityWins || 0) * 1;
        totalScore += (scores.tribeRewardWins || 0) * 1;
        totalScore += (scores.individualRewardWins || 0) * 2;
        totalScore += (scores.rightSideOfVote || 0) * 1;
        totalScore += (scores.individualImmunityWins || 0);
        totalScore += (scores.votesAgainst || 0) * -0.5;
        totalScore += (scores.idolObtained || 0) * 4;
        totalScore += (scores.advantageObtained || 0) * 2;
        totalScore += (scores.playedIdolAdvantage || 0) * 2;
        totalScore += (scores.advantageAbandoned || 0) * -2;
        totalScore += (scores.idolAbandoned || 0) * -3;
        totalScore += (scores.shotInDarkWin || 0) * 4;
        totalScore += (scores.coinFlipWin || 0) * 4;
        if (scores.finalPlacement === 1) totalScore += 25;
        if (scores.finalPlacement === 2) totalScore += 10;
        if (scores.finalPlacement === 3) totalScore += 5;
      }
    });
    return totalScore;
  };

  const calculateTeamScore = (userName: string, draft: Draft, allScores: any, episodeDeadlines: any) => {
    const { team, originalTeam, swapUsed, swapHistory = [] } = draft;
    const scoringPlan: { castawayId: number, range: { start?: number, end?: number } }[] = [];

    if (swapHistory.length > 0) {
        const swappedInIds = new Set(swapHistory.map(s => s.swappedInCastawayId));
        const initialTeam = team.filter(id => !swappedInIds.has(id));
        swapHistory.forEach(s => {
            if (!initialTeam.includes(s.swappedOutCastawayId)) {
                initialTeam.push(s.swappedOutCastawayId);
            }
        });
        
        initialTeam.forEach(id => scoringPlan.push({ castawayId: id, range: {} }));

        swapHistory.forEach(swap => {
            const swappedOutPlan = scoringPlan.find(p => p.castawayId === swap.swappedOutCastawayId);
            if (swappedOutPlan) {
                const deadline = episodeDeadlines?.[`episode${swap.episodeOfSwap}`];
                const swapEffectiveForEpisode = deadline && new Date(swap.timestamp) < new Date(deadline);
                const endEpisode = swapEffectiveForEpisode ? swap.episodeOfSwap - 1 : swap.episodeOfSwap;
                swappedOutPlan.range.end = endEpisode;
            }

            const deadline = episodeDeadlines?.[`episode${swap.episodeOfSwap}`];
            const swapEffectiveForEpisode = deadline && new Date(swap.timestamp) < new Date(deadline);
            const startEpisode = swapEffectiveForEpisode ? swap.episodeOfSwap : swap.episodeOfSwap + 1;
            scoringPlan.push({ castawayId: swap.swappedInCastawayId, range: { start: startEpisode } });
        });

    } else if (swapUsed && originalTeam) {
        const swappedOutId = originalTeam.find(id => !team.includes(id));
        const swappedInId = team.find(id => !originalTeam.includes(id));
        const unchangedIds = originalTeam.filter(id => team.includes(id));

        let effectiveEpisode = draft.swapEffectiveEpisode;
        if (!effectiveEpisode) {
            const swapTimestamp = new Date(draft.timestamp);
            const episodeNumbers = Object.keys(allScores).map(e => parseInt(e.replace('episode', ''))).sort((a, b) => a - b);
            effectiveEpisode = (episodeNumbers[episodeNumbers.length - 1] || 0) + 1;
            
            for (const epNum of episodeNumbers) {
                const deadline = episodeDeadlines?.[`episode${epNum}`];
                if (deadline && swapTimestamp < new Date(deadline)) {
                    effectiveEpisode = epNum;
                    break;
                }
            }
        }

        unchangedIds.forEach(id => scoringPlan.push({ castawayId: id, range: {} }));
        if (swappedOutId) scoringPlan.push({ castawayId: swappedOutId, range: { end: effectiveEpisode - 1 } });
        if (swappedInId) scoringPlan.push({ castawayId: swappedInId, range: { start: effectiveEpisode } });

    } else {
        team.forEach(castawayId => {
            scoringPlan.push({ castawayId, range: {} });
        });
    }
    
    const totalScore = scoringPlan.reduce((acc, item) => {
        const score = calculateScore(item.castawayId, allScores, item.range);
        return acc + score;
    }, 0);

    return { scoringPlan, totalScore };
  };

  const getCastawayName = (id: number, castawayList: Castaway[]) => castawayList.find(c => c.id === id)?.name || 'Unknown';

  const calculateEliminationPickPoints = (userName: string, eliminationPicks: any, allScores: any) => {
    let eliminationPoints = 0;
    const userPicks = eliminationPicks[userName];
    if (userPicks) {
      Object.entries(userPicks).forEach(([episode, pickedCastawayId]: [string, any]) => {
        const episodeScores = allScores[episode];
        if (episodeScores) {
          const votedOutCastawayId = Object.keys(episodeScores).find(castawayId => episodeScores[castawayId].votedOut);
          if (votedOutCastawayId && Number(votedOutCastawayId) === pickedCastawayId) {
            eliminationPoints += 3;
          }
        }
      });
    }
    return eliminationPoints;
  };

  const generateEmailHtml = (draftData: { [key: string]: Draft }, castawayData: Castaway[], scoreData: any, episode: string, eliminationPicks: any, episodeDeadlines: any) => {
    const standings = Object.entries(draftData).map(([userName, draft]) => {
      const { totalScore } = calculateTeamScore(userName, draft, scoreData, episodeDeadlines);
      const eliminationPickPoints = calculateEliminationPickPoints(userName, eliminationPicks, scoreData);
      const finalScore = totalScore + eliminationPickPoints;

      return { userName, draft, totalScore: finalScore };
    }).sort((a, b) => b.totalScore - a.totalScore).slice(0, 10);

    const tableRows = standings.map((entry, index) => `
      <tr style="background-color: ${index % 2 === 0 ? '#ffffff' : '#f9f9f9'};">
        <td style="border: 1px solid #dddddd; padding: 12px; text-align: center;">${index + 1}</td>
        <td style="border: 1px solid #dddddd; padding: 12px;"><strong>${entry.userName}</strong></td>
        <td style="border: 1px solid #dddddd; padding: 12px;">${entry.draft.team.map(id => getCastawayName(id, castawayData)).join(', ')}</td>
        <td style="border: 1px solid #dddddd; padding: 12px; text-align: center;"><strong>${entry.totalScore.toFixed(1)}</strong></td>
      </tr>
    `).join('');

    const html = `
      <div style="font-family: 'Lato', sans-serif; color: #333; border: 1px solid #bdc3c7; border-radius: 10px; overflow: hidden; box-shadow: 0 4px 8px rgba(0,0,0,0.1);">
        <div style="background-color: #2c3e50; color: #ecf0f1; padding: 20px; text-align: center;">
          <h2 style="font-family: 'Roboto Slab', serif; margin: 0; color: #e67e22; font-size: 28px;">Survivor Fantasy League Standings</h2>
        </div>
        <div style="padding: 20px;">
          <h3 style="font-family: 'Roboto Slab', serif; text-align: center; color: #34495e;">Here are your top 10 Results After Episode ${episode}</h3>
          <table style="width: 100%; border-collapse: collapse;">
            <thead>
              <tr style="background-color: #34495e; color: #ecf0f1;">
                <th style="padding: 12px; text-align: center;">Rank</th>
                <th style="padding: 12px;">Player</th>
                <th style="padding: 12px;">Team</th>
                <th style="padding: 12px; text-align: center;">Total Score</th>
              </tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
          </table>
        </div>
      </div>
    `;
    setEmailHtml(html);
  };

  return (
    <Container className="mt-5">
      <div className="mb-3">
        <Link to="/admin/dashboard" className="text-decoration-none">
          &larr; Back to Admin Home
        </Link>
      </div>
      <Card shadow-sm home-card>
        <Card.Header as="h5" className="card-title-custom text-center">Generate Standings Email (Season {adminSeason})</Card.Header>
        <Card.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          <Form.Group className="mb-3">
            <Form.Label>Select League</Form.Label>
            <Form.Select onChange={(e) => setSelectedLeagueId(e.target.value)} disabled={isLoading}>
              <option>-- Select a League --</option>
              {leagues.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Episode Number</Form.Label>
            <Form.Control type="number" placeholder="Enter episode number" value={episodeNumber} onChange={(e) => setEpisodeNumber(e.target.value)} />
          </Form.Group>
          <Button onClick={handleGenerateEmail} disabled={isLoading || !selectedLeagueId}>
            {isLoading ? <Spinner as="span" animation="border" size="sm" /> : 'Generate Email'}
          </Button>
          {emailHtml && (
            <Form.Group className="mt-3">
              <div className="d-flex justify-content-between align-items-center">
                <Form.Label>HTML Email</Form.Label>
                <Button variant="outline-secondary" size="sm" onClick={handleCopy}>
                  {isCopied ? 'Copied!' : 'Copy to Clipboard'}
                </Button>
              </div>
              <Form.Control as="textarea" rows={10} value={emailHtml} readOnly />
            </Form.Group>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default AdminStandingsEmail;
